import React, { useEffect, useMemo, useState } from "react";
import { Radio, Sparkles, Mic, Flame } from "lucide-react";

/**
 * BroadcastFrame — wraps a child (Jitsi container) with broadcast-style chrome.
 * Three themes:
 *   - "news"     · ABGN-UK Network · navy + saffron + red LIVE (default)
 *   - "podcast"  · ABS Conversations · warm burgundy + cream + amber ON AIR
 *   - "festival" · saffron + gold + lotus motifs · for Buddha Purnima etc.
 *
 * Theme can be auto-detected from the meeting title via detectTheme(title).
 */

// ──────────────────────────────────────────────────────────────────────────────
// Theme catalogue
// ──────────────────────────────────────────────────────────────────────────────
export const THEMES = {
  news: {
    id: "news",
    label: "News Channel",
    desc: "ABGN-UK Network · breaking-news look",
    icon: Radio,
    badge: { text: "Live", color: "bg-red-600/95 text-white", dotColor: "bg-white" },
    bg: "bg-[#0B1F4D]",
    topBar: "from-[#0B1F4D] via-[#0B1F4D]/95 to-transparent",
    bottomBar: "from-[#0B1F4D] to-[#0B1F4D]/85 border-saffron/40",
    tickerPill: "bg-saffron text-navy",
    tickerPillText: "ABS · Live",
    networkName: "Ambedkarite Buddhist Global Network UK",
    networkSub: "ABGN-UK Network",
    accentRing: "border-saffron/40 bg-saffron/15",
    accentIcon: "text-saffron",
    lowerThirdAccent: "from-saffron via-saffron to-saffron/80 text-navy",
    lowerThirdLabel: "Now Broadcasting",
    sidebarPanels: "news",
    cornerLabel: "ABGN-UK",
    cornerIcon: Sparkles,
    quote: {
      text: "I like the religion that teaches liberty, equality and fraternity.",
      attr: "Dr. B. R. Ambedkar",
    },
    ticker: [
      "Educate · Agitate · Organise",
      "Pragya · Karuna · Samata · Bandhutva",
      "Buddha Dhamma is humanity's message of peace and equality. — Dr. B. R. Ambedkar",
      "Welcome to the Ambedkarite Buddhist Global Network UK — global Ambedkarite Buddhist community",
      "Cultivation of mind should be the ultimate aim of human existence. — Dr. B. R. Ambedkar",
    ],
  },
  podcast: {
    id: "podcast",
    label: "Podcast Studio",
    desc: "ABS Conversations · warm host-led format",
    icon: Mic,
    badge: { text: "On Air", color: "bg-amber-500/95 text-[#3a1a0d]", dotColor: "bg-[#3a1a0d]" },
    bg: "bg-gradient-to-br from-[#2A1410] via-[#3D1F18] to-[#5C2C1F]",
    topBar: "from-[#2A1410] via-[#2A1410]/95 to-transparent",
    bottomBar: "from-[#2A1410] to-[#2A1410]/85 border-amber-400/40",
    tickerPill: "bg-amber-400 text-[#3a1a0d]",
    tickerPillText: "ABS · Audio",
    networkName: "ABS Conversations",
    networkSub: "Episode · Studio Live",
    accentRing: "border-amber-400/40 bg-amber-400/15",
    accentIcon: "text-amber-400",
    lowerThirdAccent: "from-amber-400 via-amber-400 to-amber-400/80 text-[#3a1a0d]",
    lowerThirdLabel: "Now Recording",
    sidebarPanels: "podcast",
    cornerLabel: "ABS · Audio",
    cornerIcon: Mic,
    quote: {
      text: "If you want to know a community, listen to its conversations — not its slogans.",
      attr: "ABS Conversations",
    },
    ticker: [
      "ABS Conversations · in dialogue with the Sangha",
      "Subscribe at ambedkaritebuddhistsociety.org",
      "New episode every Sunday · 8 PM GMT",
      "Listening worldwide — UK · India · USA · Canada · Australia",
      "Educate · Agitate · Organise",
    ],
  },
  festival: {
    id: "festival",
    label: "Festival",
    desc: "Saffron · lotus · for Buddha Purnima & Dhammachakra Pravartan Din",
    icon: Flame,
    badge: { text: "Festival Live", color: "bg-saffron text-navy", dotColor: "bg-navy" },
    bg: "bg-gradient-to-br from-[#3D1F00] via-[#7A3F00] to-[#D4A64A]",
    topBar: "from-[#3D1F00] via-[#3D1F00]/95 to-transparent",
    bottomBar: "from-[#3D1F00] to-[#7A3F00] border-yellow-300/60",
    tickerPill: "bg-yellow-300 text-[#3D1F00]",
    tickerPillText: "Mangala · Maitri",
    networkName: "ABS · Festival",
    networkSub: "Sabbe sattā sukhitā hontu",
    accentRing: "border-yellow-300/50 bg-yellow-300/15",
    accentIcon: "text-yellow-300",
    lowerThirdAccent: "from-yellow-300 via-saffron to-yellow-300 text-navy",
    lowerThirdLabel: "Festival Now",
    sidebarPanels: "festival",
    cornerLabel: "ABS · Festival",
    cornerIcon: Flame,
    quote: {
      text: "Sabbe sattā sukhitā hontu — may all beings be happy.",
      attr: "Mettā Sutta",
    },
    ticker: [
      "🪷  Buddhaṃ saraṇaṃ gacchāmi  ·  Dhammaṃ saraṇaṃ gacchāmi  ·  Saṅghaṃ saraṇaṃ gacchāmi  🪷",
      "Sabbe sattā sukhitā hontu — may all beings be happy",
      "Pragya · Karuna · Samata · Bandhutva",
      "Mangala · Maitri · Karuna · Mudita · Upekkha",
    ],
  },
};

// Auto-detect a sensible theme from the meeting title
export function detectTheme(title) {
  if (!title) return "news";
  const t = String(title).toLowerCase();
  if (
    t.includes("purnima") || t.includes("vesak") ||
    t.includes("dhammachakra") || t.includes("dhamma chakra") ||
    t.includes("ambedkar jayanti") || t.includes("buddha jayanti") ||
    t.includes("mahaparinirvan") || t.includes("bhima koregaon") ||
    t.includes("festival")
  ) return "festival";
  if (
    t.includes("podcast") || t.includes("conversations") ||
    t.includes("interview") || t.includes("episode") ||
    t.includes("dialogue") || t.includes("in conversation")
  ) return "podcast";
  return "news";
}

// ──────────────────────────────────────────────────────────────────────────────
// Sidebar variants
// ──────────────────────────────────────────────────────────────────────────────
function SidebarNews({ theme, title }) {
  return (
    <>
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-md p-3 text-pearl">
        <div className={`text-[9px] uppercase tracking-[0.25em] mb-1.5 ${theme.accentIcon}`}>Today's Theme</div>
        <div className="font-display text-base leading-tight">{title}</div>
      </div>
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-md p-3 text-pearl">
        <div className={`text-[9px] uppercase tracking-[0.25em] mb-1.5 ${theme.accentIcon}`}>Pillars</div>
        <ul className="text-[11px] space-y-1">
          <li className="flex justify-between"><span>Pragya</span><span className="text-pearl/55">Wisdom</span></li>
          <li className="flex justify-between"><span>Karuna</span><span className="text-pearl/55">Compassion</span></li>
          <li className="flex justify-between"><span>Samata</span><span className="text-pearl/55">Equality</span></li>
          <li className="flex justify-between"><span>Bandhutva</span><span className="text-pearl/55">Fraternity</span></li>
        </ul>
      </div>
      <div className="bg-saffron/10 backdrop-blur-md border border-saffron/30 rounded-md p-3 text-pearl text-[11px] italic leading-snug">
        "{theme.quote.text}"
        <div className="not-italic text-saffron/70 mt-1 text-[10px]">— {theme.quote.attr}</div>
      </div>
    </>
  );
}

function SidebarPodcast({ theme, title, hostName }) {
  return (
    <>
      <div className="bg-amber-400/10 backdrop-blur-md border border-amber-400/30 rounded-md p-3 text-pearl">
        <div className="text-[9px] uppercase tracking-[0.25em] text-amber-400 mb-1.5">Episode</div>
        <div className="font-display text-base leading-tight">{title}</div>
      </div>
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-md p-3 text-pearl">
        <div className="text-[9px] uppercase tracking-[0.25em] text-amber-400 mb-2">Host</div>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-amber-400/25 border border-amber-400/50 flex items-center justify-center text-amber-200 text-sm font-display flex-shrink-0">
            {(hostName || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm leading-tight truncate">{hostName || "ABS Host"}</div>
            <div className="text-[10px] text-pearl/50 uppercase tracking-widest">In studio</div>
          </div>
        </div>
      </div>
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-md p-3 text-pearl text-[11px] italic leading-snug">
        "{theme.quote.text}"
        <div className="not-italic text-amber-400/80 mt-1 text-[10px]">— {theme.quote.attr}</div>
      </div>
    </>
  );
}

function SidebarFestival({ theme, title }) {
  return (
    <>
      <div className="bg-yellow-300/10 backdrop-blur-md border border-yellow-300/40 rounded-md p-3 text-pearl">
        <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-300 mb-1.5">Festival</div>
        <div className="font-display text-base leading-tight">{title}</div>
      </div>
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-md p-3 text-pearl">
        <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-300 mb-2">Three Refuges</div>
        <ul className="text-[11px] space-y-1.5 text-pearl/85 italic">
          <li>🪷 Buddhaṃ saraṇaṃ gacchāmi</li>
          <li>🪷 Dhammaṃ saraṇaṃ gacchāmi</li>
          <li>🪷 Saṅghaṃ saraṇaṃ gacchāmi</li>
        </ul>
      </div>
      <div className="bg-yellow-300/10 backdrop-blur-md border border-yellow-300/40 rounded-md p-3 text-pearl text-[11px] italic leading-snug">
        "{theme.quote.text}"
        <div className="not-italic text-yellow-300/85 mt-1 text-[10px]">— {theme.quote.attr}</div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────
export default function BroadcastFrame({
  on = false,
  themeId = "news",
  title = "ABGN-UK Live",
  subtitle = "ABGN-UK · Global Sangha",
  hostName,
  ticker,
  children,
}) {
  const theme = THEMES[themeId] || THEMES.news;
  const [now, setNow] = useState(() => new Date());
  const tickerLines = useMemo(() => ticker || theme.ticker, [ticker, theme]);
  const tickerText = tickerLines.join("   •   ");

  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [on]);

  const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const TopIcon = theme.icon;
  const CornerIcon = theme.cornerIcon;
  const Sidebar =
    theme.sidebarPanels === "podcast" ? SidebarPodcast :
    theme.sidebarPanels === "festival" ? SidebarFestival :
    SidebarNews;

  // IMPORTANT: keep the children (Jitsi iframe anchor) at a stable DOM
  // position regardless of whether broadcast is on or off so toggling
  // doesn't unmount the iframe. We always render the same flex column
  // skeleton; only the chrome (top bar / lower-third / ticker / sidebar)
  // is conditionally rendered.

  return (
    <div className={`relative w-full h-full ${on ? theme.bg : ""} flex flex-col`} data-testid="broadcast-frame" data-broadcast-theme={on ? theme.id : "off"}>
      <style>{`
        @keyframes broadcast-marquee { 0%{transform:translateX(100%)} 100%{transform:translateX(-100%)} }
        .broadcast-marquee-track { display:inline-block; padding-left:100%; white-space:nowrap; animation: broadcast-marquee 60s linear infinite; }
        @keyframes broadcast-pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
        .broadcast-live-dot { animation: broadcast-pulse 1.6s ease-in-out infinite; }
        @keyframes broadcast-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .broadcast-festival-border {
          background: linear-gradient(90deg, transparent 0%, rgba(253, 224, 71, 0.7) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: broadcast-shimmer 6s linear infinite;
        }
      `}</style>

      {/* TOP BAR — sits ABOVE the iframe, never over it. Only shown when on. */}
      {on && (
        <div className={`h-12 bg-gradient-to-b ${theme.topBar} flex items-center justify-between px-4 sm:px-6 z-20 shrink-0`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 rounded-full ${theme.accentRing} flex items-center justify-center flex-shrink-0 border`}>
              <TopIcon size={14} className={theme.accentIcon} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-pearl text-sm leading-none truncate">{theme.networkName}</div>
              <div className={`text-[9px] uppercase tracking-[0.25em] ${theme.accentIcon}/85 leading-none mt-1`}>
                {theme.networkSub}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="hidden md:inline-flex items-center gap-1.5 bg-black/40 backdrop-blur-sm border border-white/10 px-2 py-0.5 rounded-full">
              <div className={`w-3.5 h-3.5 rounded-full ${theme.accentRing} border flex items-center justify-center`}>
                <CornerIcon size={7} className={theme.accentIcon} />
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-pearl/85 font-bold">{theme.cornerLabel}</span>
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${theme.badge.color} text-[10px] uppercase tracking-widest rounded-sm font-bold shadow`}
              data-testid="broadcast-live-badge"
            >
              <span className={`broadcast-live-dot w-1.5 h-1.5 ${theme.badge.dotColor} rounded-full`} />
              <TopIcon size={10} />
              {theme.badge.text}
            </span>
            <span className="font-mono text-pearl/85 text-sm tabular-nums hidden sm:inline">{time}</span>
          </div>
        </div>
      )}

      {/* LOWER-THIRD subtitle band — slim, sits BETWEEN top bar and iframe. */}
      {on && (
        <div
          data-testid="broadcast-lower-third"
          className="px-3 sm:px-6 py-1.5 z-20 shrink-0 flex items-center gap-3 bg-black/35 border-t border-b border-white/5"
        >
          <span className={`bg-gradient-to-r ${theme.lowerThirdAccent} px-2 py-0.5 text-[9px] uppercase tracking-[0.3em] font-bold whitespace-nowrap rounded-sm`}>
            {theme.lowerThirdLabel}
          </span>
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="font-display text-pearl text-sm leading-tight truncate">{title}</span>
            {(hostName || subtitle) && (
              <span className="text-pearl/60 text-[11px] truncate hidden sm:inline">
                {hostName ? <>· Host: <strong className={theme.accentIcon}>{hostName}</strong></> : null}
                {subtitle ? ` · ${subtitle}` : null}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Festival decorative top rim (in chrome, NOT over iframe) */}
      {on && theme.id === "festival" && (
        <div className="h-[3px] broadcast-festival-border z-10 pointer-events-none shrink-0" />
      )}

      {/* MAIN ROW — iframe + optional sidebar. STABLE across toggles so the
          Jitsi iframe inside `children` never unmounts. */}
      <div className={`flex-1 flex min-h-0 ${on ? "px-2 sm:px-3 py-2 gap-2 sm:gap-3" : ""}`}>
        <div className={`flex-1 min-w-0 ${on ? "rounded-md overflow-hidden border border-white/10 shadow-2xl" : ""}`}>
          {children}
        </div>
        {on && (
          <div className="hidden 2xl:flex w-44 flex-col gap-2 shrink-0">
            <Sidebar theme={theme} title={title} hostName={hostName} />
          </div>
        )}
      </div>

      {/* Festival decorative bottom rim */}
      {on && theme.id === "festival" && (
        <div className="h-[3px] broadcast-festival-border z-10 pointer-events-none shrink-0" />
      )}

      {/* TICKER — sits BELOW the iframe, never over it */}
      {on && (
        <div
          className={`h-9 bg-gradient-to-t ${theme.bottomBar} border-t z-20 flex items-center shrink-0`}
          data-testid="broadcast-ticker"
        >
          <div className={`flex-shrink-0 ${theme.tickerPill} px-3 h-full flex items-center font-bold text-[9px] uppercase tracking-[0.25em]`}>
            {theme.tickerPillText}
          </div>
          <div className="flex-1 overflow-hidden whitespace-nowrap">
            <span className="broadcast-marquee-track text-pearl/90 text-xs font-medium">
              {tickerText}   •   {tickerText}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
